import { useRef, useState } from 'react';
import { Alert, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { CodeHighlight } from '@mantine/code-highlight';
import { DownloadSimpleIcon, KeyIcon, ShieldCheckIcon, WarningIcon } from '@phosphor-icons/react';
import { generateEd25519KeyPair } from '@/lib/ssh-ed25519';
import { generateRsaKeyPair } from '@/lib/ssh-rsa';
import type { SshKeyPair, SshKeyType } from '@/lib/ssh-types';
import { useStrings } from '@/i18n/useLang';

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function SshKeyGenerator() {
  const t = useStrings();
  const [keyType, setKeyType] = useState<SshKeyType>('ed25519');
  const [comment, setComment] = useState('');
  const [pair, setPair] = useState<SshKeyPair | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Discards results from superseded generations (FINDING-8).
  const requestId = useRef(0);

  const generate = async () => {
    const id = ++requestId.current;
    setBusy(true);
    setError(null);
    try {
      const result =
        keyType === 'rsa'
          ? await generateRsaKeyPair(comment.trim())
          : generateEd25519KeyPair(comment.trim());
      if (id === requestId.current) {
        setPair(result);
      }
    } catch {
      if (id === requestId.current) {
        setError(t.ssh.error);
        setPair(null);
      }
    } finally {
      if (id === requestId.current) {
        setBusy(false);
      }
    }
  };

  return (
    <Stack gap="lg">
      <Alert color="cyan" icon={<ShieldCheckIcon size={18} />} variant="light" title={t.ssh.securityTitle}>
        {t.ssh.securityBody}
      </Alert>

      <Select
        label={t.ssh.keyType}
        value={keyType}
        onChange={(v) => v && setKeyType(v as SshKeyType)}
        allowDeselect={false}
        data={[
          { value: 'ed25519', label: t.ssh.ed25519 },
          { value: 'rsa', label: t.ssh.rsa },
        ]}
        description={keyType === 'ed25519' ? t.ssh.ed25519Hint : t.ssh.rsaHint}
        maw={320}
      />

      <TextInput
        label={t.ssh.comment}
        placeholder={t.ssh.commentPlaceholder}
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        maw={320}
      />

      <Button
        leftSection={<KeyIcon size={18} />}
        onClick={generate}
        loading={busy}
        size="md"
        maw={320}
      >
        {busy ? t.ssh.generating : t.ssh.generate}
      </Button>

      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}

      {pair && !busy && (
        <Stack gap="md">
          <div>
            <Text fw={600} mb={4}>
              {t.ssh.fingerprint}
            </Text>
            <Text ff="monospace" size="sm" style={{ wordBreak: 'break-all' }}>
              {pair.fingerprint}
            </Text>
          </div>

          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={600}>{t.ssh.publicKey}</Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<DownloadSimpleIcon size={16} />}
                onClick={() => downloadText(pair.publicFilename, `${pair.publicKey}\n`)}
              >
                {t.ssh.download}
              </Button>
            </Group>
            <CodeHighlight code={pair.publicKey} language="text" className="code-wrap" />
          </div>

          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={600}>{t.ssh.privateKey}</Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<DownloadSimpleIcon size={16} />}
                onClick={() => downloadText(pair.privateFilename, pair.privateKey)}
              >
                {t.ssh.download}
              </Button>
            </Group>
            <CodeHighlight code={pair.privateKey} language="text" className="code-wrap" />
            <Text size="xs" c="dimmed" mt={6}>
              {t.ssh.passphraseHint}
            </Text>
          </div>
        </Stack>
      )}
    </Stack>
  );
}
